import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Truck, ShieldCheck, Sparkles, Quote } from 'lucide-react';
import { CATEGORIES, TESTIMONIALS, HERO_IMAGES } from '../mock';
import { fetchProducts } from '../lib/api';
import ProductCard from '../components/ProductCard';
import { motion, useScroll, useTransform, useReducedMotion } from 'framer-motion';
import Magnetic from '../components/motion/Magnetic';
import ShadeSpotlight from '../components/ShadeSpotlight';
import GoldParticles from '../components/GoldParticles';
import PhotoTestimonials from '../components/PhotoTestimonials';
import logoFull from '../assets/logo_full.webp';
import model1 from '../assets/model1.webp';
import model2 from '../assets/model2.webp';
import model3 from '../assets/model3.webp';
import model4 from '../assets/model4.webp';
import braidsImg from '../assets/braids.jpg';
import ownerImg from '../assets/owner_new.jpeg';
import showcasePink from '../assets/showcase_pink.png';
import showcaseBrown from '../assets/showcase_brown.png';
import look1 from '../assets/look1.webp';
import look2 from '../assets/look2.webp';
import look3 from '../assets/look3.webp';
import look4 from '../assets/look4.webp';
import braid1 from '../assets/braid1.webp';
import braid2 from '../assets/braid2.webp';
import braid3 from '../assets/braid3.jpeg';
import braid4 from '../assets/braid4.jpeg';
import braid5 from '../assets/braid5.webp';
import braid6 from '../assets/braid6.webp';
import look6 from '../assets/look6.webp';
import look7 from '../assets/look7.webp';
import look8 from '../assets/look8.webp';
import look9 from '../assets/lookbook_a.webp';
import look10 from '../assets/lookbook_b.webp';
import look11 from '../assets/lookbook_c.webp';
import signatureImg from '../assets/luxury_collections.webp';
import signatureReignSale from '../assets/signature_reign_sale.webp';
import queensChoiceSale from '../assets/queens_choice_sale.webp';

// Alternated so braid models are never next to each other
const SHOWCASE = [showcaseBrown, braid1, look1, braid2, showcasePink, braid3, look2, braid4, look3, braid5, look4, braid6, look9, look6, look10, look7, look11, look8];

const HERO_COLLAGE = [
  { src: model1, label: 'Curly Blonde' },
  { src: model2, label: 'Sleek Straight' },
  { src: braidsImg, label: 'Braids' },
  { src: model3, label: 'Body Wave' },
  { src: model4, label: 'Long Layered' },
];

const fadeUp = {
  hidden: { opacity: 0, y: 80, scale: 0.92, filter: 'blur(10px)' },
  show: (i = 0) => ({ opacity: 1, y: 0, scale: 1, filter: 'blur(0px)', transition: { duration: 1.0, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] } }),
};

const Reveal = ({ children, i = 0, className, direction = 'up' }) => {
  const dist = 90;
  const hidden = direction === 'left'
    ? { opacity: 0, x: -dist, filter: 'blur(10px)' }
    : direction === 'right'
    ? { opacity: 0, x: dist, filter: 'blur(10px)' }
    : { opacity: 0, y: 80, scale: 0.92, filter: 'blur(10px)' };
  const variants = {
    hidden,
    show: (idx = 0) => ({ opacity: 1, x: 0, y: 0, scale: 1, filter: 'blur(0px)', transition: { duration: 1.0, delay: idx * 0.12, ease: [0.22, 1, 0.36, 1] } }),
  };
  return (
    <motion.div className={className} variants={variants} custom={i} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }}>
      {children}
    </motion.div>
  );
};

const MotionLink = motion(Link);

const _DEAL_IMG = 'https://static.prod-images.emergentagent.com/jobs/21a05e27-1cb3-4e9b-9a17-4bf2532d5b1a/images/';
const BUNDLE_DEALS = [
  { title: '3-Bundle Deal', desc: 'Three matching bundles for a full, natural install.', price: '$249', was: '$297', img: _DEAL_IMG + '0c115379970e9371649bdaf24f7b6fdbc1e6ec82a44a5ccf9898d4c6787758c2.jpeg' },
  { title: '3 Bundles + Closure', desc: 'Three bundles paired with a 5x5 HD closure.', price: '$289', was: '$339', img: _DEAL_IMG + '8c0af996db9a16dca0d83a41507cf3bef47034f3a9d9b75e4724044c7c9652be.jpeg' },
  { title: '4 Bundles + Frontal', desc: 'Four bundles with a 13x4 HD frontal for full coverage.', price: '$369', was: '$435', img: _DEAL_IMG + 'ef0134b3c20d8e11e2a3b21585ceb415bb75a1ae842bb7669bc23012ebd86441.jpeg' },
];

const Home = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [featured, setFeatured] = useState([]);
  const reduce = useReducedMotion();
  const { scrollY } = useScroll();
  const heroYRaw = useTransform(scrollY, [0, 700], [0, 150]);
  const heroY = reduce ? 0 : heroYRaw;
  useEffect(() => {
    fetchProducts().then((all) => setFeatured(all.filter((p) => p.bestseller).slice(0, 4))).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO — collage of five hairstyles with logo overlay */}
      <section className="relative h-[92vh] min-h-[600px] bg-[#0b0b0b] overflow-hidden">
        <motion.div style={{ y: heroY }} className="absolute -inset-x-0 -top-[10%] h-[120%] grid grid-cols-2 md:grid-cols-5">
          {HERO_COLLAGE.map((item, i) => (
            <div key={i} className={`relative overflow-hidden ${i === 4 ? 'hidden md:block' : ''}`}>
              <motion.img src={item.src} alt={item.label}
                initial={{ scale: 1.28, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 1.5, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                className="w-full h-full object-cover object-top" />
              <div className="absolute inset-0 bg-[#0b0b0b]/25" />
            </div>
          ))}
        </motion.div>
        {/* darkening + vignette so the logo reads clearly */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0b0b0b] via-[#0b0b0b]/45 to-[#0b0b0b]/70" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(11,11,11,0.15),rgba(11,11,11,0.75))]" />
        <GoldParticles />

        <div className="relative h-full flex flex-col items-center justify-center text-center px-4 sm:px-6">
          <div className="rise-in flex flex-col items-center max-w-3xl">
            <div className="relative mb-6 w-[440px] sm:w-[640px] lg:w-[972px] max-w-[94vw]">
              <img src={logoFull} alt="Nikki's Next Level Hair and Beauty" className="w-full h-auto object-contain drop-shadow-2xl" />
              <div aria-hidden="true" className="absolute inset-0 hero-sheen pointer-events-none" data-testid="hero-logo-sheen"
                style={{ WebkitMaskImage: `url(${logoFull})`, maskImage: `url(${logoFull})`, WebkitMaskSize: 'contain', maskSize: 'contain', WebkitMaskRepeat: 'no-repeat', maskRepeat: 'no-repeat', WebkitMaskPosition: 'center', maskPosition: 'center' }} />
            </div>
            <p className="font-body text-[#e6ddc9] text-base sm:text-lg leading-relaxed mb-9 max-w-xl">
              Glueless luxury units, itch-free braiding hair and raw virgin bundles — crafted for the woman who refuses to blend in.
            </p>
            <div className="flex flex-wrap gap-3.5 sm:gap-4 justify-center max-w-2xl">
              <Magnetic><Link to="/shop/bundles" data-testid="hero-btn-bundles" className="btn-gold px-8 py-3.5 inline-block"><span>Shop Our Bundle Collection</span></Link></Magnetic>
              <Magnetic><Link to="/shop/wigs" data-testid="hero-btn-wigs" className="btn-gold px-8 py-3.5 inline-block"><span>Shop Our Wig Collection</span></Link></Magnetic>
              <Magnetic><Link to="/shop/frontals" data-testid="hero-btn-frontals" className="btn-gold px-8 py-3.5 inline-block"><span>Shop Our Frontals</span></Link></Magnetic>
              <Magnetic><Link to="/shop/braiding-hair" data-testid="hero-btn-braids" className="btn-gold px-8 py-3.5 inline-block"><span>Shop Our Braid Collection</span></Link></Magnetic>
              <Magnetic><Link to="/shop/accessories" data-testid="hero-btn-accessories" className="btn-gold px-8 py-3.5 inline-block"><span>Shop Our Accessories</span></Link></Magnetic>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE — inverted (gold background, dark text) */}
      <div className="bg-gold-sheen border-y border-[#0b0b0b]/20 py-4 overflow-hidden">
        <div className="marquee-track whitespace-nowrap">
          {Array.from({ length: 2 }).map((_, i) => (
            <span key={i} className="inline-flex items-center">
              {['Free U.S. Shipping Over $300', '100% Virgin Human Hair', 'Glueless & Beginner Friendly', 'Itch-Free Braiding Hair', 'Ethically Sourced'].map((t) => (
                <span key={t} className="inline-flex items-center overline text-[#231a08] text-[0.68rem] mx-8">
                  <Sparkles size={13} className="text-[#4a3410] mr-3" /> {t}
                </span>
              ))}
            </span>
          ))}
        </div>
      </div>

      {/* SALE OF THE WEEK */}
      <section className="bg-[#0b0b0b] py-20 sm:py-28" data-testid="sale-of-the-week">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <p className="overline text-gold-gradient mb-3">Limited Time · Ends Sunday</p>
            <h2 className="font-display text-4xl sm:text-6xl text-[#f7f3ec]">Sale of the Week</h2>
            <div className="gold-line w-24 mx-auto mt-6" />
            <p className="font-body text-sm text-[#9c9587] mt-4 max-w-lg mx-auto">Handpicked luxury units at queen-worthy prices — while stocks last.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 lg:gap-8 max-w-4xl mx-auto">
            {[
              { img: signatureReignSale, to: '/shop/wigs#grid-signature-reign', label: 'Signature Reign', dir: 'left' },
              { img: queensChoiceSale, to: "/shop/wigs#grid-queens-choice", label: "Queen's Choice", dir: 'right' },
            ].map((b, i) => (
              <Reveal key={b.label} i={i} direction={b.dir}>
                <Link to={b.to} data-testid={`sale-banner-${i + 1}`}
                  className="group block relative overflow-hidden rounded-lg ring-1 ring-[#c6a15b]/30 shadow-2xl">
                  <img src={b.img} alt={`${b.label} Collection — 15% Off`} className="w-full h-auto block img-zoom" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 py-20 sm:py-28">
        <div className="text-center mb-14">
          <p className="overline text-[#a8823c] mb-3">Handpicked This Month</p>
          <h2 className="font-display text-4xl sm:text-6xl text-[#1a1a1a]">Featured Products</h2>
          <div className="gold-line w-24 mx-auto mt-6" />
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {featured.map((p, i) => <Reveal key={p.id} i={i}><ProductCard product={p} index={i} /></Reveal>)}
        </div>
        <div className="text-center mt-14">
          <Link to="/shop" className="inline-flex items-center gap-2 overline text-[#a8823c] hover:gap-3 transition-all">
            View All Products <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* BUNDLE DEALS */}
      <section className="bg-[#f2ece0] py-20 sm:py-28 border-t border-[#e6ddcd]" data-testid="bundle-deals-section">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <p className="overline text-[#a8823c] mb-3">Save More Together</p>
            <h2 className="font-display text-4xl sm:text-6xl text-[#1a1a1a]">Bundle Deals</h2>
            <p className="font-body text-[#6b6459] mt-4 max-w-xl mx-auto">Curated multi-bundle sets at a special price — designed for a full, flawless install. Dropping soon.</p>
            <div className="gold-line w-24 mx-auto mt-6" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 lg:gap-8">
            {/* ACTIVE DEAL — Braiding Hair Buy 4 Get 1 Free */}
            <motion.div data-testid="bundle-deal-active-braids"
              variants={fadeUp} custom={0} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }}
              className="sm:col-span-3 relative overflow-hidden bg-[#0b0b0b] border border-[#c6a15b]/50 p-8 sm:p-10 flex flex-col sm:flex-row items-center gap-6 sm:gap-10 text-center sm:text-left">
              <span className="absolute top-4 right-4 overline text-[0.55rem] text-[#0b0b0b] bg-[#e7d3a1] px-2.5 py-1">Live Deal</span>
              <div className="flex-shrink-0">
                <span className="font-display text-6xl sm:text-7xl text-gold-gradient leading-none">Buy 4</span>
                <span className="block font-display text-3xl sm:text-4xl text-[#e7d3a1] mt-1">Get 1 Free</span>
              </div>
              <div className="flex-1">
                <h3 className="font-display text-3xl text-[#f7f3ec] leading-tight">Braiding Hair Deal</h3>
                <p className="font-body text-sm text-[#9c9587] mt-3 max-w-lg">
                  Add any 5 packs of X-Pression pre-stretched braiding hair to your bag and your cheapest pack is automatically <span className="text-[#e7d3a1]">FREE</span> at checkout — no code needed.
                </p>
                <Magnetic className="inline-block mt-6">
                  <Link to="/shop/braiding-hair" className="btn-gold inline-flex px-8 py-3.5" data-testid="shop-braiding-deal-btn">
                    <span className="flex items-center gap-2">Shop Braiding Hair <ArrowRight size={15} /></span>
                  </Link>
                </Magnetic>
              </div>
            </motion.div>
            {BUNDLE_DEALS.map((d, idx) => (
              <motion.div key={d.title} data-testid={`bundle-deal-${d.title}`}
                variants={fadeUp} custom={idx} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }}
                className="relative overflow-hidden bg-[#0b0b0b] border border-[#c6a15b]/25 p-8 flex flex-col items-center text-center min-h-[380px] justify-end transition-colors hover:border-[#c6a15b]/60">
                <img src={d.img} alt={d.title} className="absolute inset-0 w-full h-full object-cover img-zoom" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0b0b0b] via-[#0b0b0b]/70 to-[#0b0b0b]/25" />
                <span className="absolute top-4 right-4 z-10 overline text-[0.55rem] text-[#e7d3a1] border border-[#c6a15b]/40 bg-[#0b0b0b]/60 px-2.5 py-1">Coming Soon</span>
                <div className="relative z-10 flex flex-col items-center">
                  <h3 className="font-display text-2xl text-[#f7f3ec] leading-tight">{d.title}</h3>
                  <p className="font-body text-sm text-[#cfc7b8] mt-3 max-w-[240px]">{d.desc}</p>
                  <div className="mt-6 flex items-end gap-2">
                    <span className="font-display text-3xl text-[#e7d3a1]">{d.price}</span>
                    <span className="font-body text-sm text-[#a39c8e] line-through mb-1">{d.was}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="bg-[#141311] py-20 sm:py-28">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <p className="overline text-gold-gradient mb-3">Curated Collections</p>
            <h2 className="font-display text-4xl sm:text-6xl text-[#f7f3ec]">Shop By Category</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 lg:gap-5">
            {CATEGORIES.map((c, idx) => (
              <MotionLink key={c.slug} to={`/shop/${c.slug}`}
                variants={fadeUp} custom={idx} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }}
                className="group relative h-[340px] lg:h-[420px] overflow-hidden">
                <img src={c.image} alt={c.name} className="w-full h-full object-cover img-zoom" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0b0b0b] via-[#0b0b0b]/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5 text-center">
                  <h3 className="font-display text-2xl lg:text-[1.75rem] text-[#f7f3ec] mb-1.5 leading-tight">{c.name}</h3>
                  <p className="font-body text-xs text-[#cfc7b8] mb-4 leading-snug">{c.tagline}</p>
                  <span className="inline-flex items-center gap-1.5 overline text-[0.6rem] text-[#e7d3a1] border-b border-[#c6a15b]/50 pb-1 group-hover:gap-2.5 transition-all">
                    Shop <ArrowRight size={13} />
                  </span>
                </div>
              </MotionLink>
            ))}
          </div>
        </div>
      </section>

      {/* LUXURY COLLECTIONS BANNER */}
      <section className="relative bg-[#0b0b0b] py-10 sm:py-16 overflow-hidden" data-testid="luxury-collections-banner">
        <div className="relative max-w-[1500px] mx-auto px-4 sm:px-6">
          <Reveal>
            <div className="relative rounded-xl overflow-hidden shadow-2xl ring-1 ring-[#c6a15b]/25">
              <img src={signatureImg} alt="The Luxury Collections — Imperial Luxe & Silk Dynasty by Nikki's Next Level"
                className="w-full h-auto block" />
            </div>
          </Reveal>
          {/* Shop Collection button (matches the bundle-page style) */}
          <div className="flex justify-center mt-8 sm:mt-10">
            <Magnetic>
              <Link to="/shop/bundles" data-testid="shop-collection-btn" className="btn-gold inline-block px-9 sm:px-12 py-4 text-sm">
                <span className="flex items-center gap-2">Shop the Collection <ArrowRight size={16} /></span>
              </Link>
            </Magnetic>
          </div>
        </div>
      </section>

      {/* SHADE SPOTLIGHT — auto-cycling bundle shades */}
      <ShadeSpotlight />

      {/* TESTIMONIALS — scrolling 3D cards */}
      <PhotoTestimonials items={TESTIMONIALS} subtitle="Loved By Thousands" title="The Next Level Experience" dark={false} variant="cards" />

      {/* VALUE PROPS */}
      <section className="bg-[#141311] py-16">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 grid sm:grid-cols-3 gap-10">
          {[
            { icon: Truck, title: 'Fast Shipping', text: 'Free U.S. shipping on orders over $300.' },
            { icon: ShieldCheck, title: 'Premium Quality', text: '100% virgin human hair, quality-checked and guaranteed to last.' },
            { icon: Sparkles, title: 'Beginner Friendly', text: 'Glueless, pre-plucked units designed to install in minutes.' },
          ].map((v) => (
            <div key={v.title} className="text-center">
              <v.icon size={30} className="text-[#c6a15b] mx-auto mb-4" />
              <h4 className="font-display text-2xl text-[#f7f3ec] mb-2">{v.title}</h4>
              <p className="font-body text-sm text-[#9c9587] max-w-xs mx-auto leading-relaxed">{v.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* MEET THE OWNER */}
      <section className="relative bg-[#0b0b0b] overflow-hidden py-20 sm:py-28">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(198,161,91,0.14),transparent_55%)]" />
        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Portrait */}
          <Reveal direction="left" className="relative mx-auto lg:mx-0 max-w-md w-full">
            <div className="absolute -inset-3 border border-[#c6a15b]/40 translate-x-4 translate-y-4" />
            <div className="relative overflow-hidden">
              <img src={ownerImg} alt="Brooke N. Knight, Founder of Nikki's Next Level" className="w-full h-full object-cover img-zoom" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0b0b0b]/40 to-transparent" />
            </div>
            <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 lg:left-8 lg:translate-x-0 bg-gold-sheen px-6 py-3 shadow-xl">
              <p className="font-serif-alt text-[#231a08] text-lg tracking-wide whitespace-nowrap">Brooke N. Knight</p>
              <p className="overline text-[#4a3410] text-[0.55rem] text-center lg:text-left">Founder &amp; CEO</p>
            </div>
          </Reveal>

          {/* Bio */}
          <Reveal direction="right" className="lg:pl-4 pt-8 lg:pt-0">
            <p className="overline text-gold-gradient mb-4">Meet The Owner</p>
            <h2 className="font-display text-4xl sm:text-6xl text-[#f7f3ec] leading-[1.05] mb-6">
              The visionary behind the <span className="italic text-gold-gradient">brand.</span>
            </h2>
            <p className="font-body text-[#cfc7b8] leading-loose mb-6">
              <span className="font-medium text-[#e7d3a1]">Nikki’s Next Level LLC.</span> is a beauty and hair company founded by Brooke N. Knight in 2017, driven by the desire to empower women through access to high-quality hair and beauty products.
            </p>
            <p className="font-body text-[#9c9587] leading-loose mb-8">
              Our brand offers premium virgin hair, affordable luxury options, and economy-friendly selections that cater to a diverse customer base — including stylists, salons, individuals with hair loss, and everyday beauty enthusiasts. We aim to provide premium products that enhance both confidence and appearance.
            </p>
            <div className="flex items-center gap-4 border-l-2 border-[#c6a15b] pl-5">
              <Quote size={26} className="text-[#c6a15b] shrink-0" />
              <p className="font-display text-2xl sm:text-3xl text-[#f7f3ec] italic">“Where Empowerment Meets Beauty.”</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SHOWCASE MARQUEE */}
      <section className="bg-[#141311] py-14 sm:py-16 overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 text-center mb-10">
          <p className="overline text-gold-gradient">The Look Book</p>
        </div>
        <div className="relative group">
          <div className="marquee-track items-center" style={{ animationDuration: '39s' }}>
            {[...SHOWCASE, ...SHOWCASE].map((src, i) => (
              <div key={i} className="h-[360px] sm:h-[440px] mx-3 shrink-0 overflow-hidden shadow-lg">
                <img src={src} alt="Nikki's Next Level style" className="h-full w-auto object-contain" />
              </div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
};

export default Home;
